import { ApiError, apiRequest, apiRequestWithEtag, type IEtagged } from '@/domains/api';
import type {
  ICreateRole,
  IPermissionInfo,
  IRole,
  IRoleDetail,
  IRoleEvent,
  IUpdateRole
} from './role-contracts';

/**
 * survey-api's Team Portal roles — `/api/v1/admin/permissions` and `/api/v1/admin/roles*`
 * (SR-228, its spec §6.1), all `roles.manage`. The sole reader of those seven routes.
 *
 * No module-level promise, unlike the SEC ADV catalogue: a role is edited on Setup without a
 * release, and a held read would draw last hour's set. A caller without `roles.manage` does not
 * call any of them — the screens draw what the account's own document carries instead.
 *
 * **A role's revision is the server's, quoted back as read** — the work items' and the refresh
 * schedule's rule, as survey-api built it (the role-model UI design's §9, 2): the detail, a create
 * and an edit carry it as the `ETag`, which an edit and a delete send as `If-Match`; it is never
 * parsed. Nothing here judges
 * a role — its code's pattern, a permission the caller may give, the last role manager — all of
 * which survey-api refuses with a problem the screen says.
 *
 * **A role id is a GUID (`role_id uuid`), and anything else is refused before a request** — the
 * refresh run's rule: it comes from the address bar and is spliced into a path, and `roles/.`
 * would reach the list.
 */
const ADMIN_BASE = '/api/v1/admin';

const ROLE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a value can name a role — asked before any request that splices it into a path. */
export function isRoleId(value: string): boolean {
  return ROLE_ID.test(value);
}

function rolePath(roleId: string): string {
  if (!isRoleId(roleId)) throw new Error(`Not a role id: ${roleId}`);
  return `${ADMIN_BASE}/roles/${roleId}`;
}

export const rolesApi = {
  /** The permission catalogue, in survey-api's order (a bare array, §9 item 3 as built). */
  permissions(signal?: AbortSignal): Promise<IPermissionInfo[]> {
    return apiRequest<IPermissionInfo[]>(`${ADMIN_BASE}/permissions`, { signal });
  },

  /** Every role, in survey-api's order (a bare array, unpaged — §9 item 4 as built). */
  listRoles(signal?: AbortSignal): Promise<IRole[]> {
    return apiRequest<IRole[]>(`${ADMIN_BASE}/roles`, { signal });
  },

  /** One role with its holders and its `ETag`, or `null` for no such role — a 404, or an id that
   *  is not a GUID, answered here unsent. */
  async role(roleId: string, signal?: AbortSignal): Promise<Nullable<IEtagged<IRoleDetail>>> {
    if (!isRoleId(roleId)) return null;
    try {
      return await apiRequestWithEtag<IRoleDetail>(rolePath(roleId), { signal });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /** 201 with the role made — its detail, of which only `roleId` is read. */
  createRole(body: ICreateRole): Promise<IRole> {
    return apiRequest<IRole>(`${ADMIN_BASE}/roles`, { method: 'POST', body });
  },

  /** The whole role but its code, under `If-Match`. Whatever it answers, the page reads the role
   *  again (§9, 7). */
  updateRole(roleId: string, body: IUpdateRole, etag: string): Promise<unknown> {
    return apiRequest<unknown>(rolePath(roleId), {
      method: 'PUT',
      body,
      headers: { 'If-Match': etag }
    });
  },

  /** Under `If-Match`; refused 409 `role-in-use` while any account holds it. */
  deleteRole(roleId: string, etag: string): Promise<unknown> {
    return apiRequest<unknown>(rolePath(roleId), {
      method: 'DELETE',
      headers: { 'If-Match': etag }
    });
  },

  /** Every `role_event` row of the role, newest first — unpaged (§9 item 5 as built); a deleted
   *  role's history still answers. */
  history(roleId: string, signal?: AbortSignal): Promise<IRoleEvent[]> {
    return apiRequest<IRoleEvent[]>(`${rolePath(roleId)}/history`, { signal });
  }
};
